import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  Typography,
  Tooltip,
  Button,
  Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { NoteDto } from '../../../api-client';
import { observer } from "mobx-react";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { ExpandLess, ExpandMore, WarningAmber } from "@mui/icons-material";
import { formatDateDisplay, formatTime } from "../../../helpers/dateFormat";
import { useDeleteNote } from '../../../stores/Notes/notes.mutations';
import WarningIcon from "@mui/icons-material/Warning";

interface NotesProps {
  notes: NoteDto[];
  setNotes: (index: number, updatedCard: NoteDto) => void;
  maxVisibleNotes: number;
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * Focus the "Neuer Hinweis" field on mount. Off by default: inline hosts
   * (patient column, Rx forms) mount several of these alongside other content,
   * where stealing the caret — and scrolling the field into view — is never
   * what the user asked for. Opt in only where the note field IS the reason
   * the host opened, e.g. the Hinweise dialog.
   */
  autoFocus?: boolean;
  /**
   * What this note is attached to, singular — "Terminhinweis", "Rezepthinweis",
   * "Patientenhinweis".
   *
   * The component is shared by all three owners and cannot tell which one it is
   * hosted by, so the host names it. Without this, creating and editing say a
   * generic "Hinweis" while the surrounding heading says something specific,
   * which is exactly the ambiguity this is meant to remove.
   *
   * Defaults to the generic term so hosts that have not been given an owner yet
   * read as they always did.
   */
  noteKind?: string;
}

/**
 * Compact auto-growing note textarea (shadcn). Replaces the MUI TextField
 * (multiline minRows/maxRows), whose label/padding infrastructure made the
 * field noticeably taller and its font larger than the note text it creates.
 * Grows with content from one line up to `maxRows` lines, then scrolls.
 */
const NoteTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { maxRows?: number }
>(({ maxRows = 4, className, onChange, ...props }, ref) => {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const setRefs = (el: HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
  };
  const LINE_HEIGHT = 20; // text-sm line height (px)
  const VPAD = 12; // py-1.5 top+bottom
  const resize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    const max = maxRows * LINE_HEIGHT + VPAD;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  };
  // Keep height in sync when the value is set programmatically (e.g. cleared).
  useEffect(() => {
    if (innerRef.current) resize(innerRef.current);
  });
  return (
    <Textarea
      ref={setRefs}
      rows={1}
      onChange={(e) => {
        resize(e.currentTarget);
        onChange?.(e);
      }}
      className={cn("min-h-0 resize-none px-2 py-1.5 leading-5", className)}
      {...props}
    />
  );
});
NoteTextarea.displayName = "NoteTextarea";

const Notes: React.FC<NotesProps> = observer(({ notes, setNotes, maxVisibleNotes, onDirtyChange, autoFocus = false, noteKind = "Hinweis" }) => {
  const { mutateAsync: deleteNoteById } = useDeleteNote();
  const [cards, setCards] = useState(notes);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [newNoteText, setNewNoteText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const newNoteRef = useRef<HTMLTextAreaElement>(null);

  // Notify parent when dirty state changes
  useEffect(() => {
    onDirtyChange?.(newNoteText.trim().length > 0 || editingIndex !== null);
  }, [newNoteText, editingIndex, onDirtyChange]);

  useEffect(() => {
    setCards(notes);
  }, [notes]);

  useEffect(() => {
    if (autoFocus) newNoteRef.current?.focus();
  }, [autoFocus]);

  const notesToShow = (() => {
    const noteCards = cards || [];
    if (maxVisibleNotes < 3) return noteCards;
    return expanded || noteCards.length <= maxVisibleNotes
      ? noteCards
      : noteCards.slice(0, maxVisibleNotes);
  })();

  const handleCreateNote = () => {
    const trimmed = newNoteText.trim();
    if (!trimmed) return;
    const newCard: NoteDto = { text: trimmed };
    const updatedCards = [newCard, ...cards];
    setCards(updatedCards);
    setNotes(0, newCard);
    setNewNoteText('');
    // Re-focus the input after saving
    setTimeout(() => newNoteRef.current?.focus(), 0);
  };

  const handleNewNoteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreateNote();
    }
  };

  const handleEditCard = (index: number) => {
    setEditingIndex(index);
    setEditedContent(cards[index].text || '');
  };

  const handleSaveCard = (index: number) => {
    const updatedCard = { ...cards[index], text: editedContent };
    const updatedCards = [...cards];
    updatedCards[index] = updatedCard;
    setCards(updatedCards);
    setNotes(index, updatedCard);
    setEditingIndex(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveCard(index);
    }
    if (e.key === 'Escape') {
      setEditingIndex(null);
    }
  };

  const handleDeleteCard = async (index: number) => {
    const noteToDelete = cards[index];
    const updatedCards = [...cards];
    updatedCards.splice(index, 1);
    setCards(updatedCards);
    await deleteNoteById({
      noteId: noteToDelete.id as string,
      patientId: noteToDelete.patientId ? noteToDelete.patientId.toString() : undefined,
      appointmentId: noteToDelete.appointmentId ? noteToDelete.appointmentId.toString() : undefined,
    });
  };

  return (
    // Slight own padding only — hosts (FlatCard Hinweise sections, tab panels)
    // bring their own inset; doubling it squeezed the narrow Rx column.
    <Box sx={{ maxHeight: "70vh", display: "flex", flexDirection: "column", p: 0.5 }}>
      {/* New note input — always visible at top */}
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mb: 1 }}>
        <NoteTextarea
          ref={newNoteRef}
          maxRows={4}
          placeholder={`Neuer ${noteKind} …`}
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          onKeyDown={handleNewNoteKeyDown}
        />
        <Tooltip title={`${noteKind} speichern`}>
          <span>
            <IconButton
              color="success"
              size="small"
              onClick={handleCreateNote}
              disabled={!newNoteText.trim()}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Notes list */}
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {notesToShow?.map((card, index) => {
          const isSystem = !!card.isSystemNote || card.kind === "system";
          return (
          <Box
            key={card.id || index}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              py: 0.75,
              px: 0.75,
              borderRadius: "8px",
              transition: "background-color 0.15s",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            {/* Header line: identity, date and the (hover) actions share ONE
                row, so the note text below gets the full card width — the old
                avatar/action side columns squeezed the text into a narrow
                middle strip. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 24 }}>
              {isSystem ? (
                <WarningIcon color="error" sx={{ width: 20, height: 20 }} />
              ) : (
                <MobiliAvatar
                  user={card.updatedByUser || card.createdByUser}
                  sx={{ width: 22, height: 22, fontSize: "0.65rem" }}
                />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ flex: 1, minWidth: 0 }}
              >
                {isSystem
                  ? "Systemhinweis"
                  : card.createdAt
                    ? `${formatDateDisplay(new Date(card.createdAt))} ${formatTime(card.createdAt)}`
                    : "Jetzt"}
              </Typography>
              {/* Edit-mode actions live here too, keeping the textarea full width. */}
              {!isSystem && editingIndex === index && (
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  <Tooltip title="Speichern">
                    <IconButton
                      size="small"
                      sx={{ p: 0.25 }}
                      onClick={() => handleSaveCard(index)}
                      color="primary"
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abbrechen">
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setEditingIndex(null)}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {!isSystem && editingIndex !== index && (
                <Box
                  sx={{
                    display: "flex",
                    flexShrink: 0,
                    opacity: hoveredIndex === index ? 1 : 0,
                    transition: "opacity 0.15s",
                  }}
                >
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleEditCard(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleDeleteCard(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {/* Body — full width of the note row */}
            {!isSystem && editingIndex === index ? (
              <NoteTextarea
                autoFocus
                maxRows={6}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                onKeyDown={(e) => handleEditKeyDown(e, index)}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  ...(isSystem
                    ? { fontWeight: 500 }
                    : { cursor: "pointer", "&:hover": { color: "primary.main" } }),
                }}
                onClick={isSystem ? undefined : () => handleEditCard(index)}
              >
                {card.text || `Bitte klicken Sie, um einen ${noteKind} zu schreiben`}
              </Typography>
            )}
          </Box>
          );
        })}

        {maxVisibleNotes >= 3 && cards && cards.length > maxVisibleNotes && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Button size="small" onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
              {expanded ? "Weniger anzeigen" : `${cards.length - maxVisibleNotes} weitere`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default Notes;
